/**
 * Arma lo que recibe el carrusel de imágenes de /aseos (`ImageCarouselModal`).
 *
 * La columna «Imágenes» del aseo trae solo URLs (`record.imagenes: string[]`), así que el
 * carrusel no sabía cuáles eran videos con versión liviana. Pero la misma fila trae, en
 * `tareas[].imagenes`, los entries completos que firmó el backend (`url`, `thumbUrl`,
 * `previewUrl`, `kind`...), y desde 2026-10-27 también `imagenesDetalle` (el mismo conjunto ya
 * sin repetidos). Acá se cruza cada URL con su entry para entregarle al carrusel la URL completa
 * y la `previewUrl` del video (que abre en 480p con selector Baja/HD).
 *
 * Es solo presentación y solo reutiliza URLs que la fila ya tenía: no pide ni firma nada.
 */
import type { ImagenCarrusel } from "./components/ImageCarouselModal";

/** Elemento del carrusel con el tipo del archivo (`kind`: el carrusel decide «video» con él). */
export type ImagenCarruselDetalle = Extract<ImagenCarrusel, object>;

interface EntryConUrl {
  url?: string | null;
  previewUrl?: string | null;
  kind?: string;
}

const esEntry = (x: unknown): x is EntryConUrl =>
  !!x && typeof x === "object" && typeof (x as EntryConUrl).url === "string";

/** url → entry, a partir de `imagenesDetalle` y de `tareas[].imagenes` de las filas dadas. */
function entriesPorUrl(
  filas: ReadonlyArray<{ tareas?: unknown; imagenesDetalle?: unknown }>,
): Map<string, EntryConUrl> {
  const porUrl = new Map<string, EntryConUrl>();
  const agregar = (lista: unknown) => {
    if (!Array.isArray(lista)) return;
    for (const e of lista) {
      if (esEntry(e) && e.url && !porUrl.has(e.url)) porUrl.set(e.url, e);
    }
  };
  for (const fila of filas) {
    agregar(fila?.imagenesDetalle);
    if (Array.isArray(fila?.tareas)) {
      for (const t of fila.tareas) {
        if (t && typeof t === "object") agregar((t as { imagenes?: unknown }).imagenes);
      }
    }
  }
  return porUrl;
}

/**
 * @param items - lo que pasa la columna: URLs (string) o entries `{ url, previewUrl? }`.
 * @param filas - filas de aseos cargadas (de donde sale el detalle de cada URL).
 * @returns elementos para el carrusel, sin vacíos ni repetidos, en el orden recibido.
 */
export function imagenesParaCarrusel(
  items: unknown,
  filas: ReadonlyArray<{ tareas?: unknown; imagenesDetalle?: unknown }>,
): ImagenCarruselDetalle[] {
  if (!Array.isArray(items)) return [];
  const porUrl = entriesPorUrl(filas);
  const vistos = new Set<string>();
  const out: ImagenCarruselDetalle[] = [];
  for (const it of items) {
    const url = typeof it === "string" ? it : esEntry(it) ? it.url : null;
    if (!url || vistos.has(url)) continue;
    vistos.add(url);
    const propio = typeof it === "string" ? null : it;
    const entry = porUrl.get(url);
    const previewUrl = (propio as EntryConUrl | null)?.previewUrl || entry?.previewUrl || null;
    const kind = (propio as EntryConUrl | null)?.kind || entry?.kind;
    out.push({ url, previewUrl, ...(kind ? { kind } : {}) });
  }
  return out;
}
